/**
 * 权限管理工具类
 * 提供用户权限检查、角色管理等功能
 */

import Vue from 'vue';

/** 供 Vue 组件响应式订阅（permissionManager 内部数据非响应式） */
export const permissionState = Vue.observable({
  ready: false,
  version: 0,
  permissions: [],
  roleId: null,
  /** 当前账号可见设备数（服务端 devicelist 统计） */
  deviceScopeCount: null,
  dataScopeAdminIds: [],
  tenantOwnerUid: null,
  isSubaccount: false,
  /** 最近一次从服务端加载的摘要，便于排查 */
  lastFetch: null,
});

/** 登录用户主键（兼容 id / uid） */
function resolveUserId(user) {
  if (!user) return null;
  const raw = user.id != null && user.id !== '' ? user.id : user.uid;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function resolveApiUri() {
  if (typeof window !== 'undefined' && window.Vue?.prototype?.apiuri) {
    return window.Vue.prototype.apiuri;
  }
  let domain = typeof document !== 'undefined' ? document.domain : '';
  if (process.env.NODE_ENV === 'development') {
    domain = 'adminv3.aicoiot.com';
  }
  if (!domain) {
    return null;
  }
  return `https://${domain}/api.php`;
}

function logPermissionLoaded(source, payload) {
  permissionState.lastFetch = {
    at: Date.now(),
    source,
    ...payload,
  };
  if (process.env.NODE_ENV !== 'production') {
    console.info('[RBAC] 用户权限已加载', permissionState.lastFetch);
  }
}

// 权限常量定义
export const PERMISSIONS = {
  // 设备管理权限
  DEVICE_VIEW: 'device.view',
  DEVICE_EDIT: 'device.edit',
  DEVICE_DELETE: 'device.delete',
  DEVICE_ADD: 'device.add',
  
  // 订单管理权限
  ORDER_VIEW: 'order.view',
  ORDER_EDIT: 'order.edit',
  ORDER_DELETE: 'order.delete',
  ORDER_PRINT: 'order.print',
  
  // 用户管理权限
  USER_VIEW: 'user.view',
  USER_EDIT: 'user.edit',
  USER_DELETE: 'user.delete',
  USER_ADD: 'user.add',
  
  // 管理员管理权限
  ADMIN_VIEW: 'admin.view',
  ADMIN_EDIT: 'admin.edit',
  ADMIN_DELETE: 'admin.delete',
  ADMIN_ADD: 'admin.add',
  
  // 系统设置权限
  SYSTEM_VIEW: 'system.view',
  SYSTEM_EDIT: 'system.edit',
  
  // 商品管理权限
  GOODS_VIEW: 'goods.view',
  GOODS_EDIT: 'goods.edit',
  GOODS_DELETE: 'goods.delete',
  GOODS_ADD: 'goods.add',
  
  // 营销管理权限
  MARKETING_VIEW: 'marketing.view',
  MARKETING_EDIT: 'marketing.edit',
  
  // 微信管理权限
  WECHAT_VIEW: 'wechat.view',
  WECHAT_EDIT: 'wechat.edit',
  
  // 权限管理权限
  PERMISSION_VIEW: 'permission.view',
  PERMISSION_EDIT: 'permission.edit',
  PERMISSION_DELETE: 'permission.delete',
  
  // 超级管理员权限
  SUPER_ADMIN: '*'
};

// 角色常量定义
export const ROLES = {
  SUPER_ADMIN: 0,    // 超级管理员
  ADMIN: 1,          // 管理员
  OPERATOR: 2,       // 操作员
  VIEWER: 3,         // 只读用户
  STORE: 4           // 门店（设备范围由超级管理员分配）
};

// 角色权限映射
const ROLE_PERMISSIONS = {
  0: ['*'], // 超级管理员拥有所有权限
  1: [
    'device.view',
    'device.edit',
    'device.add',
    'order.view',
    'order.edit',
    'order.print',
    'user.view',
    'user.edit',
    'user.add',
    'admin.view',
    'admin.edit',
    'admin.add',
    'goods.view',
    'goods.edit',
    'goods.add',
    'marketing.view',
    'wechat.view',
    'system.view',
    'permission.view',
    'permission.edit',
    'permission.delete'
  ],
  2: [
    'device.view',
    'order.view',
    'order.edit',
    'order.print',
    'user.view',
    'user.edit',
    'goods.view',
    'goods.edit'
  ],
  3: [
    'device.view',
    'order.view',
    'user.view',
    'goods.view'
  ],
  4: [
    'device.view',
    'device.edit',
    'order.view',
    'order.edit',
    'order.print',
    'goods.view',
    'goods.edit'
  ]
};

// 权限描述映射
export const PERMISSION_DESCRIPTIONS = {
  'device.view': '查看设备',
  'device.edit': '编辑设备',
  'device.delete': '删除设备',
  'device.add': '添加设备',
  'order.view': '查看订单',
  'order.edit': '编辑订单',
  'order.delete': '删除订单',
  'order.print': '打印订单',
  'user.view': '查看用户',
  'user.edit': '编辑用户',
  'user.delete': '删除用户',
  'user.add': '添加用户',
  'admin.view': '查看管理员',
  'admin.edit': '编辑管理员',
  'admin.delete': '删除管理员',
  'admin.add': '添加管理员',
  'system.view': '查看系统设置',
  'system.edit': '编辑系统设置',
  'goods.view': '查看商品',
  'goods.edit': '编辑商品',
  'goods.delete': '删除商品',
  'goods.add': '添加商品',
  'marketing.view': '查看营销',
  'marketing.edit': '编辑营销',
  'wechat.view': '查看微信',
  'wechat.edit': '编辑微信',
  'permission.view': '查看权限',
  'permission.edit': '编辑权限',
  'permission.delete': '删除权限',
  '*': '超级管理员'
};

// 角色描述映射
export const ROLE_DESCRIPTIONS = {
  0: '超级管理员',
  1: '管理员',
  2: '操作员',
  3: '只读用户',
  4: '门店'
};

/**
 * 权限管理器类
 */
class PermissionManager {
  constructor() {
    this.user = null;
    this.permissions = [];
    this.roleId = null;
    this.deviceScopeCount = null;
    this.dataScopeAdminIds = [];
    this.tenantOwnerUid = null;
    this.isSubaccount = false;
    this.roles = []; // 存储从服务器获取的角色列表
    this.permissionCache = {}; // 权限缓存
    this.apiuri = null;
    this._readyPromise = null;
  }

  /**
   * 清理用户权限（用于退出登录时）
   */
  clearUser() {
    this.user = null;
    this.permissions = [];
    this.roleId = null;
    this.roles = [];
    this.permissionCache = {};
    this._readyPromise = null;
    this.deviceScopeCount = null;
    this.dataScopeAdminIds = [];
    this.tenantOwnerUid = null;
    this.isSubaccount = false;
    permissionState.ready = false;
    permissionState.permissions = [];
    permissionState.roleId = null;
    permissionState.deviceScopeCount = null;
    permissionState.dataScopeAdminIds = [];
    permissionState.tenantOwnerUid = null;
    permissionState.isSubaccount = false;
    permissionState.lastFetch = null;
    permissionState.version += 1;
  }

  /** 调试用：查看是否已从服务端拉取权限 */
  getPermissionDebugInfo() {
    return {
      ready: permissionState.ready,
      roleId: permissionState.roleId,
      permissions: [...permissionState.permissions],
      lastFetch: permissionState.lastFetch,
    };
  }

  _syncState(markReady) {
    permissionState.permissions = [...this.permissions];
    permissionState.roleId = this.roleId;
    permissionState.deviceScopeCount = this.deviceScopeCount;
    permissionState.dataScopeAdminIds = [...(this.dataScopeAdminIds || [])];
    permissionState.tenantOwnerUid = this.tenantOwnerUid;
    permissionState.isSubaccount = this.isSubaccount;
    if (markReady) {
      permissionState.ready = true;
    }
    permissionState.version += 1;
  }

  /**
   * 重新加载用户权限（用于用户切换、登录后）
   */
  reloadUser() {
    this._readyPromise = null;
    return this.ensureReady();
  }

  /**
   * 从服务端拉取角色与当前用户有效权限（路由守卫须 await）
   */
  ensureReady() {
    if (!localStorage.getItem('user')) {
      return Promise.resolve(false);
    }
    if (!this._readyPromise) {
      this._readyPromise = this._fetchPermissionsFromServer().catch((err) => {
        this._readyPromise = null;
        console.warn('权限加载失败，使用本地映射:', err);
        this.loadUser();
        this.permissions = this.getRolePermissions(this.roleId);
        this._syncState(true);
        return false;
      });
    }
    return this._readyPromise;
  }

  async _fetchPermissionsFromServer() {
    this.loadUser();
    if (!this.user || !this.user.openid) {
      return false;
    }
    const apiuri = this.apiuri || resolveApiUri();
    if (!apiuri) {
      console.warn('[RBAC] 无法解析 apiuri，跳过权限拉取');
      return false;
    }
    this.apiuri = apiuri;
    await this.loadPermissionsFromServer(apiuri, this.user.openid);
    const ok = await this.loadUserPermissionsFromServer(
      apiuri,
      this.user.openid,
      this.roleId
    );
    if (!ok) {
      this.permissions = this.getRolePermissions(this.roleId);
      logPermissionLoaded('fallback-roleid', {
        roleId: this.roleId,
        permissions: this.permissions,
        fromServer: false,
      });
    }
    this._syncState(true);
    return true;
  }

  /**
   * 加载用户信息
   */
  async loadUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        this.user = JSON.parse(userStr);
        
        // roleid = 权限组；topuid = 上级账号 uid（主账号为 0）；copyuser = 数据范围等同用户
        const topuid = parseInt(this.user.topuid, 10) || 0;
        this.isSubaccount = topuid > 0;

        const cachedRole = parseInt(
          this.user.roleid != null ? this.user.roleid : this.user.roleId,
          10
        );
        if (Number.isFinite(cachedRole)) {
          this.roleId = cachedRole;
        } else if (
          this.user.username === 'admin' ||
          this.user.email === 'admin@themesdesign.in'
        ) {
          this.roleId = 0;
          this.isSubaccount = false;
        } else {
          this.roleId = 3;
        }
        
        const apiuri = resolveApiUri();
        if (this.user.openid && apiuri) {
          this.apiuri = apiuri;
          this.permissions = [];
          permissionState.ready = false;
        } else if (this.user.openid) {
          this.permissions = [];
          permissionState.ready = false;
        } else {
          this.permissions = this.getRolePermissions(this.roleId);
        }
        this._syncState(permissionState.ready);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      this.user = null;
      this.roleId = 3;
      this.permissions = this.getRolePermissions(this.roleId);
      this._syncState(true);
    }
  }

  /**
   * 获取角色权限
   * @param {number} roleId 角色ID
   * @returns {Array} 权限列表
   */
  getRolePermissions(roleId) {
    // 优先从缓存中获取
    if (this.permissionCache[roleId]) {
      return this.permissionCache[roleId];
    }
    // 如果缓存中没有，使用硬编码的权限映射（作为后备）
    return ROLE_PERMISSIONS[roleId] || ROLE_PERMISSIONS[3];
  }

  /**
   * 从服务器加载角色和权限数据
   * @param {string} apiuri API 地址
   * @param {string} openid 用户 OpenID
   * @returns {Promise} 加载权限数据的 Promise
   */
  async loadPermissionsFromServer(apiuri, openid) {
    try {
      // 动态导入 axios（如果可用）
      const axios = window.Vue?.prototype?.$axios || (await import('axios')).default;
      
      // 获取所有角色列表
      const rolesResponse = await axios.post(
        apiuri,
        { action: 'getroles' },
        { headers: { Openid: openid } }
      );

      if (rolesResponse.data?.status === 200 && rolesResponse.data?.data) {
        this.applyServerRoles(rolesResponse.data.data);
      }

      // 获取所有权限列表（可选，用于权限管理页面）
      const permissionsResponse = await axios.post(
        apiuri,
        { action: 'getpermissions' },
        { headers: { Openid: openid } }
      );

      if (permissionsResponse.data?.status === 200 && permissionsResponse.data?.data) {
        // 可以在这里处理权限列表
        console.log('从服务器加载权限列表成功');
      }

      return true;
    } catch (error) {
      console.warn('从服务器加载权限数据失败，使用本地权限映射:', error);
      return false;
    }
  }

  /**
   * 从服务器加载当前用户的权限
   * @param {string} apiuri API 地址
   * @param {string} openid 用户 OpenID
   * @param {number} roleId 角色ID
   * @returns {Promise} 加载用户权限的 Promise
   */
  async loadUserPermissionsFromServer(apiuri, openid, roleId) {
    try {
      const axios = window.Vue?.prototype?.$axios || (await import('axios')).default;
      const headers = { Openid: openid };

      const cur = await axios.post(
        apiuri,
        { action: 'getcurrentuserpermissions' },
        { headers }
      );

      if (
        cur.data?.status === 200 &&
        Array.isArray(cur.data?.data?.permissions) &&
        cur.data.data.permissions.length > 0
      ) {
        this.permissions = cur.data.data.permissions;
        const rid =
          cur.data.data.role_id != null
            ? parseInt(cur.data.data.role_id, 10)
            : cur.data.data.roleid != null
              ? parseInt(cur.data.data.roleid, 10)
              : roleId;
        this.roleId = rid;
        this.permissionCache[rid] = [...this.permissions];
        if (this.user) {
          this.user.roleId = rid;
          this.user.roleid = rid;
          if (cur.data.data.topuid != null || cur.data.data.db_topuid != null) {
            this.user.topuid =
              cur.data.data.topuid != null
                ? cur.data.data.topuid
                : cur.data.data.db_topuid;
          }
          if (cur.data.data.copyuser != null) {
            this.user.copyuser = cur.data.data.copyuser;
          }
          try {
            localStorage.setItem('user', JSON.stringify(this.user));
          } catch (e) {
            /* ignore */
          }
        }
        this.deviceScopeCount =
          cur.data.data.device_scope_count != null
            ? parseInt(cur.data.data.device_scope_count, 10)
            : null;
        this.dataScopeAdminIds = Array.isArray(cur.data.data.data_scope_admin_ids)
          ? cur.data.data.data_scope_admin_ids
          : [];
        this.tenantOwnerUid = cur.data.data.tenant_owner_uid != null
          ? parseInt(cur.data.data.tenant_owner_uid, 10)
          : null;
        this.isSubaccount =
          cur.data.data.is_subaccount != null
            ? !!cur.data.data.is_subaccount
            : (parseInt(this.user?.topuid, 10) || 0) > 0;
        logPermissionLoaded('getcurrentuserpermissions', {
          roleId: rid,
          permissions: this.permissions,
          deviceScopeCount: this.deviceScopeCount,
          dataScopeAdminIds: this.dataScopeAdminIds,
          tenantOwnerUid: this.tenantOwnerUid,
          isSubaccount: this.isSubaccount,
          dbTopuid: cur.data.data.db_topuid,
          fromServer: true,
          apiStatus: cur.data.status,
        });
        this._syncState(false);
        return true;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.warn('[RBAC] getcurrentuserpermissions 未返回有效 permissions', cur.data);
      }

      const userId = resolveUserId(this.user);

      if (userId) {
        const byUser = await axios.post(
          apiuri,
          { action: 'getuserpermissions', user_id: userId },
          { headers }
        );
        if (
          byUser.data?.status === 200 &&
          Array.isArray(byUser.data?.data?.permissions) &&
          byUser.data.data.permissions.length > 0
        ) {
          const data = byUser.data.data;
          const rid =
            data.role_id != null ? parseInt(data.role_id, 10) : roleId;
          this.permissions = data.permissions;
          this.roleId = rid;
          this.permissionCache[rid] = [...this.permissions];
          logPermissionLoaded('getuserpermissions', {
            roleId: rid,
            permissions: this.permissions,
            fromServer: true,
          });
          this._syncState(false);
          return true;
        }
      }

      const response = await axios.post(
        apiuri,
        { action: 'getuserpermissions', role_id: roleId },
        { headers }
      );

      if (
        response.data?.status === 200 &&
        Array.isArray(response.data?.data?.permissions) &&
        response.data.data.permissions.length > 0
      ) {
        this.permissions = response.data.data.permissions;
        this.permissionCache[roleId] = [...this.permissions];
        this._syncState(false);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('从服务器加载用户权限失败，使用本地权限映射:', error);
      return false;
    }
  }

  /**
   * 应用后台返回的角色列表（写入缓存，供 getAllRoles / 权限检查使用）
   */
  applyServerRoles(roles) {
    if (!roles || !Array.isArray(roles) || roles.length === 0) return;
    this.roles = roles;
    for (const role of roles) {
      if (role.permissions && Array.isArray(role.permissions)) {
        this.permissionCache[role.id] = [...role.permissions];
      }
    }
  }

  /**
   * 检查是否拥有指定权限
   * @param {string} permission 权限名称
   * @returns {boolean} 是否拥有权限
   */
  hasPermission(permission) {
    if (!permission) return true;

    const perms = permissionState.permissions;

    if (localStorage.getItem('user') && !permissionState.ready) {
      return false;
    }

    if (perms.includes('*')) {
      return true;
    }

    return perms.includes(permission);
  }

  /**
   * 检查是否拥有所有指定权限
   * @param {Array} permissions 权限列表
   * @returns {boolean} 是否拥有所有权限
   */
  hasAllPermissions(permissions) {
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * 检查是否拥有任意一个指定权限
   * @param {Array} permissions 权限列表
   * @returns {boolean} 是否拥有任意一个权限
   */
  hasAnyPermission(permissions) {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * 检查用户角色
   * @param {number} roleId 角色ID
   * @returns {boolean} 是否拥有指定角色
   */
  hasRole(roleId) {
    return parseInt(permissionState.roleId, 10) === parseInt(roleId, 10);
  }

  /**
   * 检查是否为超级管理员
   * @returns {boolean} 是否为超级管理员
   */
  isSuperAdmin() {
    return this.hasRole(0);
  }

  /**
   * 检查是否为管理员
   * @returns {boolean} 是否为管理员
   */
  isAdmin() {
    return this.hasRole(1) || this.isSuperAdmin();
  }

  /**
   * 获取用户当前权限列表
   * @returns {Array} 权限列表
   */
  getUserPermissions() {
    return [...permissionState.permissions];
  }

  /**
   * 获取用户角色信息
   * @returns {Object} 角色信息
   */
  getUserRole() {
    return {
      id: this.roleId,
      name: ROLE_DESCRIPTIONS[this.roleId],
      permissions: this.permissions
    };
  }

  /**
   * 更新用户权限（用于动态权限更新）
   * @param {Array} permissions 新的权限列表
   */
  updatePermissions(permissions) {
    this.permissions = permissions || [];
  }

  /**
   * 更新用户角色
   * @param {number} roleId 新的角色ID
   */
  updateRole(roleId) {
    this.roleId = roleId;
    if (this.user) {
      this.user.roleid = roleId;
      this.user.roleId = roleId;
    }
    this.permissions = this.getRolePermissions(roleId);
    
    // 更新localStorage中的用户信息
    if (this.user) {
      this.user.roleId = roleId;
      localStorage.setItem('user', JSON.stringify(this.user));
    }
  }

  /**
   * 刷新权限（重新加载用户信息）
   */
  refresh() {
    this.loadUser();
  }

  /**
   * 获取所有可用权限
   * @returns {Array} 所有权限列表
   */
  getAllPermissions() {
    return Object.keys(PERMISSION_DESCRIPTIONS).filter(p => p !== '*');
  }

  /**
   * 获取所有角色
   * @returns {Array} 所有角色列表
   */
  getAllRoles() {
    // 如果从服务器加载了角色数据，优先使用
    if (this.roles && this.roles.length > 0) {
      return this.roles.map(role => ({
        id: role.id,
        name: role.name || ROLE_DESCRIPTIONS[role.id] || '未知角色',
        description: role.description || '',
        permissions: role.permissions || this.getRolePermissions(role.id)
      }));
    }
    
    // 否则使用本地硬编码的角色列表
    return [
      { id: 0, name: ROLE_DESCRIPTIONS[0], permissions: ROLE_PERMISSIONS[0] },
      { id: 1, name: ROLE_DESCRIPTIONS[1], permissions: ROLE_PERMISSIONS[1] },
      { id: 2, name: ROLE_DESCRIPTIONS[2], permissions: ROLE_PERMISSIONS[2] },
      { id: 3, name: ROLE_DESCRIPTIONS[3], permissions: ROLE_PERMISSIONS[3] },
      { id: 4, name: ROLE_DESCRIPTIONS[4], permissions: ROLE_PERMISSIONS[4] }
    ];
  }

  /**
   * 检查权限是否有效
   * @param {string} permission 权限名称
   * @returns {boolean} 权限是否有效
   */
  isValidPermission(permission) {
    return Object.keys(PERMISSION_DESCRIPTIONS).includes(permission);
  }

  /**
   * 检查角色是否有效
   * @param {number} roleId 角色ID
   * @returns {boolean} 角色是否有效
   */
  isValidRole(roleId) {
    const id = parseInt(roleId, 10);
    if (Object.keys(ROLE_DESCRIPTIONS).includes(String(id))) {
      return true;
    }
    if (this.roles && this.roles.length > 0) {
      return this.roles.some((r) => parseInt(r.id, 10) === id);
    }
    return false;
  }
}

// 创建全局权限管理器实例
export const permissionManager = new PermissionManager();

// 导出权限管理器类（用于测试或创建新实例）
export default PermissionManager;
